const { Core, Utils, Events } = Global;
const { $fetch, parseHTML } = Utils;
const WINTER_SALE_GIFT_VARIANT_ID = 42579388366946;


customElements.define('cart-provider', class extends Core {
    liveSections = new Set();
    notificationsEnabled = false;

    render() {
        this.sub(Events.CART_REGISTER, this._registerSection, { global: true });
        this.sub(Events.CART_NOTIFICATIONS_ENABLING, this._enableNotifications, { global: true });
        this.sub(Events.CART_ADD, this._addToCart, { global: true });
        this.sub(Events.CART_CHANGE, this._updateCart, { global: true });
        this.sub(Events.CART_REPLACE, this._replaceCartItem, { global: true });
        this.sub(Events.PRODUCT_FORM_SUBMIT, this._formSubmitHandler, { global: true });

        document.addEventListener('click', this._handleWinterSaleGiftClick.bind(this));
        document.addEventListener('keydown', this._handleWinterSaleGiftKeydown.bind(this));
        this._getWinterSaleGiftSelector()?.addEventListener('cancel', this._handleWinterSaleGiftCancel.bind(this));
        this._setExternalAPI();
        this._scheduleWinterSaleGiftSync();
    }

    _registerSection(section) {
        this.liveSections.add(section);
    }
    
    _enableNotifications() {
        this.notificationsEnabled = true;
    }

    async _formSubmitHandler({ formData }) {
        this.notificationsEnabled && formData.append("attributes[notification_items_variant_ids]", formData.get('id'));
        formData.append("sections", Array.from(this.liveSections).join(','));

        const config = {
            method: 'POST',
            headers: {
                "X-Requested-With": "XMLHttpRequest",
                "Accept": "application/javascript"
            },
            body: formData
        }
        
        try {
            const res = await fetch(`${window.dynamicURLs['cartAdd']}.js`, config);
            
            const cartData = await res.json();

            if(!res.ok) {
                this.pub(Events.TOAST_NOTIFICATION, {
                    type: 'error',
                    msg: cartData.description,
                    title: cartData.message
                });
                throw new Error(cartData.description);
            }
            this.pub(Events.CART_UPDATE, {
                sections: cartData.sections,
                req: "cartAdd"
            })
            this._scheduleWinterSaleGiftSync();
        } catch (error) {
            console.error(error);
            this.pub(Events.CART_ERROR, error);
        } finally {
            this.notificationsEnabled && this._cleanCartAttributes();
        }
    }
    
    async _addToCart({ items }) {
        await this._requestCart('cartAdd', {
            items,
            ...(this.notificationsEnabled && { attributes: {
                notification_items_variant_ids: items.map(({id}) => id).join(',')
            }})
        }, items.map(item => item.id));
        this._cleanCartAttributes();
    }

    _cleanCartAttributes() {
        this._fetchCart('cartUpdate', { attributes: { notification_items_variant_ids: null }});
    }

    _fetchCart(route, body) {
        return fetch(`${window.dynamicURLs[route]}.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(
                body
            )
        })
    }

    async _updateCart({ quantity, line }) {
        // NOTE: Terrible, but forced to use line (index) because of unexpected shopify cart API behavior.    
        // TODO: Deprecate line usage    
        await this._requestCart('cartChange', {
            line,
            quantity
        });
    }

    async _replaceCartItem({line, newItem, quantity}) {
        await this._fetchCart('cartChange', {
            line, quantity: 0
        });
        this._addToCart({items: [{ id: newItem, quantity }]});
    }

    async _requestCart(route, body) {
        try {
            const res = await this._fetchCart(route, {
                ...body,
                sections: Array.from(this.liveSections).join(','),
            });
            
            const cartData = await res.json();

            if(!res.ok) {
                this.pub(Events.TOAST_NOTIFICATION, {
                    type: 'error',
                    msg: cartData.description,
                    title: cartData.message
                });
                throw new Error(cartData.description);
            }

            this.pub(Events.CART_UPDATE, {
                sections: cartData.sections,
                req: route
            })
            this._scheduleWinterSaleGiftSync();

        } catch (error) {
            console.error(error);
            this.pub(Events.CART_ERROR, error);
        }
    }

    _scheduleWinterSaleGiftSync(delay = 0) {
        clearTimeout(this._winterSaleGiftTimer);
        this._winterSaleGiftTimer = setTimeout(() => this._syncWinterSaleGift(), delay);
    }

    _getWinterSaleConfig() {
        const enabled = this.dataset.winterSaleEnabled === 'true';
        const threshold = Number(this.dataset.winterSaleThreshold);
        const selector = this._getWinterSaleGiftSelector();
        const configuredVariantIds = selector
            ? Array.from(selector.querySelectorAll('[data-winter-sale-gift-variant]'))
                .map((option) => Number(option.dataset.winterSaleGiftVariant))
                .filter(Boolean)
            : [];
        const fallbackVariantId = Number(this.dataset.winterSaleGiftVariantId) || WINTER_SALE_GIFT_VARIANT_ID;
        const variantIds = configuredVariantIds.length > 0 ? configuredVariantIds : [fallbackVariantId].filter(Boolean);

        if (!enabled || !threshold || variantIds.length === 0) {
            return null;
        }

        return {
            threshold,
            variantIds,
            requiresSelection: configuredVariantIds.length > 0,
            title: this.dataset.winterSaleGiftTitle || 'gift',
            label: this.dataset.winterSaleLabel || 'Free gift'
        };
    }

    async _syncWinterSaleGift() {
        const config = this._getWinterSaleConfig();
        if (!config || this._winterSaleGiftSyncing) return;
        if (this._winterSaleGiftBlockedUntil && Date.now() < this._winterSaleGiftBlockedUntil) return;

        this._winterSaleGiftSyncing = true;

        try {
            const cart = await this._getCart();
            const giftLines = [];
            const qualifyingTotal = cart.items.reduce((total, item) => {
                if (this._isWinterSaleGiftItem(item)) {
                    giftLines.push(item);
                    return total;
                }
                return total + (item.final_line_price ?? item.line_price ?? 0);
            }, 0);
            const eligible = qualifyingTotal >= config.threshold;

            if (!eligible) {
                this._setWinterSaleGiftOptOut(false, config);
                this._closeWinterSaleGiftSelector(false);

                if (giftLines.length > 0) {
                    await this._removeWinterSaleGift(giftLines);
                    this._refreshCart();
                }
                return;
            }

            if (eligible && giftLines.length === 0) {
                if (this._hasWinterSaleGiftOptOut(config)) return;

                if (config.requiresSelection) {
                    this._openWinterSaleGiftSelector();
                    return;
                }

                await this._addWinterSaleGift(config, { id: config.variantIds[0] });
                this._refreshCart();
                return;
            }

            if (eligible && giftLines.length > 0) {
                this._closeWinterSaleGiftSelector(false);
                const changed = await this._normalizeWinterSaleGift(giftLines);
                changed && this._refreshCart();
                return;
            }

        } catch (error) {
            console.error(error);
        } finally {
            this._winterSaleGiftSyncing = false;
        }
    }

    async _getCart() {
        const res = await fetch(`${window.dynamicURLs['cart']}.js`, {
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!res.ok) {
            throw new Error('Unable to fetch cart');
        }

        return res.json();
    }

    _isWinterSaleGiftItem(item) {
        const properties = item.properties || {};
        return String(properties._winter_sale_gift).toLowerCase() === 'true';
    }

    async _addWinterSaleGift({ variantIds, title, label }, selection) {
        const variantId = Number(selection && selection.id);
        if (!variantId || !variantIds.includes(variantId)) {
            throw new Error('Please choose an available gift option.');
        }

        const properties = {
            _winter_sale_gift: 'true',
            _winter_sale_offer: `${label} free ${title}`
        };

        if (selection.gender) properties._winter_sale_style = selection.gender;
        if (selection.size) properties._winter_sale_size = selection.size;

        const res = await this._fetchCart('cartAdd', {
            items: [{
                id: variantId,
                quantity: 1,
                properties
            }]
        });

        if (!res.ok) {
            this._winterSaleGiftBlockedUntil = Date.now() + 30000;
            let message = 'Unable to add that gift. Please choose another option.';
            try {
                const error = await res.json();
                message = error.description || error.message || message;
            } catch (err) {}
            throw new Error(message);
        }

        await this._ensureWinterSaleGiftIsFree(variantId);
    }

    async _ensureWinterSaleGiftIsFree(variantId) {
        const cart = await this._getCart();
        const giftLine = cart.items.find((item) =>
            Number(item.variant_id) === variantId && this._isWinterSaleGiftItem(item)
        );

        if (!giftLine) {
            throw new Error('The selected gift could not be found in your cart. Please try again.');
        }

        if (Number(giftLine.final_line_price) > 0) {
            await this._changeWinterSaleGift(giftLine.key, 0);
            this._winterSaleGiftBlockedUntil = Date.now() + 30000;
            this._refreshCart();
            throw new Error('The free-gift discount is not active yet, so the gift was removed and you have not been charged for it.');
        }
    }

    async _normalizeWinterSaleGift(giftLines) {
        let changed = false;

        for (const [index, item] of giftLines.entries()) {
            const quantity = index === 0 ? 1 : 0;
            if (item.quantity !== quantity) {
                await this._changeWinterSaleGift(item.key, quantity);
                changed = true;
            }
        }

        return changed;
    }

    async _removeWinterSaleGift(giftLines) {
        for (const item of giftLines) {
            await this._changeWinterSaleGift(item.key, 0);
        }
    }

    async _changeWinterSaleGift(key, quantity) {
        const res = await this._fetchCart('cartChange', {
            id: key,
            quantity
        });

        if (!res.ok) {
            throw new Error('Unable to update the free gift.');
        }
    }

    _refreshCart() {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    _handleWinterSaleGiftClick(e) {
        const selector = this._getWinterSaleGiftSelector();
        if (selector && e.target === selector) {
            e.preventDefault();
            this._setWinterSaleGiftOptOut(true);
            this._closeWinterSaleGiftSelector();
            return;
        }

        const closeButton = e.target.closest('[data-winter-sale-gift-close]');
        if (selector && closeButton && selector.contains(closeButton)) {
            e.preventDefault();
            this._setWinterSaleGiftOptOut(true);
            this._closeWinterSaleGiftSelector();
            return;
        }

        const productOption = e.target.closest('[data-winter-sale-gift-product-option]');
        if (selector && productOption && selector.contains(productOption)) {
            e.preventDefault();
            this._selectWinterSaleGiftProduct(productOption);
            return;
        }

        const variantOption = e.target.closest('[data-winter-sale-gift-variant]');
        if (selector && variantOption && selector.contains(variantOption)) {
            e.preventDefault();
            this._selectWinterSaleGiftVariant(variantOption);
            return;
        }

        const confirmButton = e.target.closest('[data-winter-sale-gift-confirm]');
        if (selector && confirmButton && selector.contains(confirmButton)) {
            e.preventDefault();
            this._confirmWinterSaleGiftSelection();
            return;
        }

        if (e.target.closest('[data-winter-sale-gift-remove]')) {
            this._setWinterSaleGiftOptOut(true);
            return;
        }

        if (e.target.closest('[data-winter-sale-gift-add]')) {
            e.preventDefault();
            this._setWinterSaleGiftOptOut(false);
            if (!this._openWinterSaleGiftSelector()) {
                this._scheduleWinterSaleGiftSync(0);
            }
        }
    }

    _getWinterSaleGiftSelector() {
        return this.querySelector('[data-winter-sale-gift-selector]');
    }

    _openWinterSaleGiftSelector() {
        const selector = this._getWinterSaleGiftSelector();
        if (!selector || selector.querySelectorAll('[data-winter-sale-gift-variant]:not(:disabled)').length === 0) {
            return false;
        }

        if (selector.open) return true;

        this._winterSaleGiftReturnFocus = document.activeElement;
        this._resetWinterSaleGiftSelector();
        selector.showModal();
        document.documentElement.classList.add('winter-gift-selector-open');
        selector.querySelector('.winter-gift-selector__dialog')?.focus();
        return true;
    }

    _closeWinterSaleGiftSelector(restoreFocus = true) {
        const selector = this._getWinterSaleGiftSelector();
        if (!selector || !selector.open) return;

        selector.close();
        document.documentElement.classList.remove('winter-gift-selector-open');

        if (restoreFocus && this._winterSaleGiftReturnFocus?.isConnected) {
            this._winterSaleGiftReturnFocus.focus();
        }
        this._winterSaleGiftReturnFocus = null;
    }

    _resetWinterSaleGiftSelector() {
        const selector = this._getWinterSaleGiftSelector();
        if (!selector) return;

        selector.querySelectorAll('[data-winter-sale-gift-product-option], [data-winter-sale-gift-variant]')
            .forEach((option) => option.setAttribute('aria-pressed', 'false'));
        selector.querySelectorAll('[data-winter-sale-gift-sizes]').forEach((group) => group.hidden = true);
        const sizeStep = selector.querySelector('[data-winter-sale-gift-size-step]');
        if (sizeStep) sizeStep.hidden = true;
        const confirm = selector.querySelector('[data-winter-sale-gift-confirm]');
        if (confirm) {
            confirm.disabled = true;
            confirm.dataset.loading = 'false';
        }
        const label = selector.querySelector('[data-winter-sale-gift-confirm-label]');
        if (label) label.textContent = 'Add free gift to my cart';
        this._setWinterSaleGiftStatus('');

        const productOptions = Array.from(selector.querySelectorAll('[data-winter-sale-gift-product-option]:not(:disabled)'));
        if (productOptions.length === 1) {
            this._selectWinterSaleGiftProduct(productOptions[0], false);
        }
    }

    _selectWinterSaleGiftProduct(option, moveFocus = true) {
        const selector = this._getWinterSaleGiftSelector();
        if (!selector || option.disabled) return;

        const wasSelected = option.getAttribute('aria-pressed') === 'true';
        const productOptions = selector.querySelectorAll('[data-winter-sale-gift-product-option]');
        const variantOptions = selector.querySelectorAll('[data-winter-sale-gift-variant]');
        const sizeGroups = selector.querySelectorAll('[data-winter-sale-gift-sizes]');
        const sizeStep = selector.querySelector('[data-winter-sale-gift-size-step]');
        const selectedStyle = selector.querySelector('[data-winter-sale-gift-selected-style]');
        const confirm = selector.querySelector('[data-winter-sale-gift-confirm]');

        if (wasSelected) {
            productOptions.forEach((item) => item.setAttribute('aria-pressed', 'false'));
            variantOptions.forEach((item) => item.setAttribute('aria-pressed', 'false'));
            sizeGroups.forEach((group) => group.hidden = true);
            if (sizeStep) sizeStep.hidden = true;
            if (selectedStyle) selectedStyle.textContent = '';
            if (confirm) confirm.disabled = true;
            this._setWinterSaleGiftStatus('');
            return;
        }

        productOptions.forEach((item) => item.setAttribute('aria-pressed', String(item === option)));
        variantOptions.forEach((item) => item.setAttribute('aria-pressed', 'false'));

        const productKey = option.dataset.winterSaleGiftProductOption;
        sizeGroups.forEach((group) => group.hidden = group.dataset.winterSaleGiftSizes !== productKey);

        const availableVariants = Array.from(selector.querySelectorAll(`[data-winter-sale-gift-sizes="${productKey}"] [data-winter-sale-gift-variant]:not(:disabled)`));
        if (sizeStep) sizeStep.hidden = availableVariants.length <= 1;
        if (selectedStyle) selectedStyle.textContent = option.dataset.winterSaleGiftProductLabel || '';
        if (confirm) confirm.disabled = true;
        this._setWinterSaleGiftStatus('');

        if (availableVariants.length === 1) {
            this._selectWinterSaleGiftVariant(availableVariants[0]);
            return;
        }

        if (availableVariants.length === 0) {
            this._setWinterSaleGiftStatus('This gift is currently unavailable.');
            return;
        }

        if (moveFocus) availableVariants[0].focus();
    }

    _selectWinterSaleGiftVariant(option) {
        const selector = this._getWinterSaleGiftSelector();
        if (!selector || option.disabled) return;

        const wasSelected = option.getAttribute('aria-pressed') === 'true';
        selector.querySelectorAll('[data-winter-sale-gift-variant]')
            .forEach((item) => item.setAttribute('aria-pressed', 'false'));
        if (!wasSelected) option.setAttribute('aria-pressed', 'true');

        const confirm = selector.querySelector('[data-winter-sale-gift-confirm]');
        if (confirm) confirm.disabled = wasSelected;
        this._setWinterSaleGiftStatus('');
    }
    async _confirmWinterSaleGiftSelection() {
        const selector = this._getWinterSaleGiftSelector();
        const config = this._getWinterSaleConfig();
        const selected = selector?.querySelector('[data-winter-sale-gift-variant][aria-pressed="true"]');
        const confirm = selector?.querySelector('[data-winter-sale-gift-confirm]');
        const confirmLabel = selector?.querySelector('[data-winter-sale-gift-confirm-label]');

        if (!selector || !config || !selected || !confirm || this._winterSaleGiftSelectionAdding) {
            this._setWinterSaleGiftStatus('Please choose your gift and option.');
            return;
        }

        this._winterSaleGiftSelectionAdding = true;
        confirm.disabled = true;
        confirm.dataset.loading = 'true';
        if (confirmLabel) confirmLabel.textContent = 'Adding your free gift...';
        this._setWinterSaleGiftStatus('');

        try {
            await this._addWinterSaleGift(config, {
                id: Number(selected.dataset.winterSaleGiftVariant),
                gender: selected.dataset.winterSaleGiftGender,
                size: selected.dataset.winterSaleGiftSize
            });
            this._setWinterSaleGiftOptOut(false, config);
            this._closeWinterSaleGiftSelector(false);
            this._refreshCart();
        } catch (error) {
            console.error(error);
            this._setWinterSaleGiftStatus(error.message || 'Unable to add that gift. Please try again.');
            confirm.disabled = false;
        } finally {
            this._winterSaleGiftSelectionAdding = false;
            confirm.dataset.loading = 'false';
            if (confirmLabel) confirmLabel.textContent = 'Add free gift to my cart';
        }
    }

    _setWinterSaleGiftStatus(message) {
        const status = this._getWinterSaleGiftSelector()?.querySelector('[data-winter-sale-gift-status]');
        if (status) status.textContent = message;
    }

    _handleWinterSaleGiftKeydown(e) {
        const selector = this._getWinterSaleGiftSelector();
        if (!selector || !selector.open) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            this._setWinterSaleGiftOptOut(true);
            this._closeWinterSaleGiftSelector();
            return;
        }

        if (e.key !== 'Tab') return;

        const focusable = Array.from(selector.querySelectorAll('button:not(:disabled):not([hidden])'))
            .filter((element) => element.offsetParent !== null);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }

    _handleWinterSaleGiftCancel(e) {
        e.preventDefault();
        this._setWinterSaleGiftOptOut(true);
        this._closeWinterSaleGiftSelector();
    }

    _winterSaleGiftOptOutKey(config = this._getWinterSaleConfig()) {
        return config
            ? `winter-sale-gift-removed:v3:${config.label}:${config.threshold}`
            : 'winter-sale-gift-removed:v3';
    }

    _setWinterSaleGiftOptOut(removed, config = this._getWinterSaleConfig()) {
        if (!config) return;

        try {
            const key = this._winterSaleGiftOptOutKey(config);
            removed ? sessionStorage.setItem(key, '1') : sessionStorage.removeItem(key);
        } catch (err) {}
    }

    _hasWinterSaleGiftOptOut(config = this._getWinterSaleConfig()) {
        if (!config) return false;

        try {
            return sessionStorage.getItem(this._winterSaleGiftOptOutKey(config)) === '1';
        } catch (err) {
            return false;
        }
    }

    _setExternalAPI() {
        document.addEventListener('cart:refresh', this._cartRefresh.bind(this));
    }

    async _cartRefresh() {
        const cartSectionsReqURL = `${window.dynamicURLs['root']}?sections=${Array.from(this.liveSections).join(',')}`;

        try {
            const data = await fetch(cartSectionsReqURL);
            const sections = await data.json();

            this.pub(Events.CART_UPDATE, {
                sections
            });
            this._scheduleWinterSaleGiftSync();
        } catch (err) {
            console.error(err);
        }
    }
})

customElements.define('cart-qty', class extends Core {
    propTypes = {
        value: Number,
        key: String,
        input: Boolean,
        'prevent-default': Boolean,
        line: Number
    }

    render() {
        this.$({
            [this.evt]: this._handleEvent
        })
    }

    _handleEvent(e) {
        this.prop('prevent-default') && e.preventDefault();
        this.pub(Events.CART_CHANGE, {
            key: this.prop('key'),
            line: this.prop('line'),
            quantity: this._getValue(e)
        })
    }

    _getValue(e) {
        return this.evt === 'change'
            ? this._getValidatedValue(e)
            : this.prop('value'); 
    }

    _getValidatedValue(e) {
        const maxValue = e.target.hasAttribute('max') && +e.target.getAttribute('max');
        if (maxValue && e.target.value > maxValue) {
            e.target.value = maxValue
            return maxValue
        }
        return e.target.value
    }

    get evt() {
        return this.prop('input') 
            ? 'change' 
            : 'click';
    }
});

customElements.define('cart-live-region', class extends Core {
    propTypes = {
        'target-section': String,
        'target-element': String
    }

    render() {
        this.targetSection = this.prop('target-section') || this.sectionId;
        this.targetSelector = this.prop('target-element') || `#${this.id}`;

        customElements.whenDefined('cart-provider').then(() => {
            this.pub(Events.CART_REGISTER, this.targetSection)
        })

        this.sub(Events.CART_UPDATE, this._updateContent, { global: true })
    }
    
    _updateContent({ sections }) {
        const targetSection = sections && sections[this.targetSection];
        if(!targetSection) {
            console.error(`target section ${targetSection} do not exists`);
            return;
        }

        const doc = Utils.parseHTML(targetSection);
        Utils.$replaceContent(this, doc.querySelector(this.targetSelector));
    }
})
